import React, { useCallback, useEffect } from "react";
import { SafeAreaView, View, StyleSheet, ScrollView, Linking } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { Grid, Container, Row, Column } from "ui-grid";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { RootState } from "../../redux/reducers";
import { DomusoApiService } from "../../api/DomusoApiService";
import { translate } from "../../i18n";
import { typography, Text, theme, Button, ButtonType, Icon, fontWeights } from "ui-components";
import {
  setPaymentCreated,
  setDefaultPaymentSourceUpdated,
} from "../../redux/actions/actionCreators";
import { Spacer } from "../../components/spacer/spacer";
import { HomeParamsList, HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { formatDate, getDisplayableLocalTime } from "../../utils/date";
import { toHumanDollars } from "../../utils/money";
import { AllowedPaymentMethods } from "../../enums";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import { CheckType } from "../../enums/check";
import { AppReviewModal } from "../../components/modal/app-review-modal";
import { getUnitNumber } from "../../utils";
import { convertHexToRGBA } from "../../utils/general";
import { PaymentSubmittedPointsEarnedCard } from "../../components/new/payment-flow/payment-submitted-points-earned-card";
import { UserAppState } from "../../enums/user-app-state";

interface OwnProps {}

export type Props = OwnProps;

export const PaymentFlowPaymentTrasmitted = ({ route }: HomeScreenProps) => {
  const logger = useSelector((state: RootState) => state.logger);
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);

  const navigation = useNavigation();
  const { params = {} as HomeParamsList } = route;
  let {
    propName,
    propertyUnit,
    paymentDate,
    isCertified,
    isCertifiedBA,
    isUnapprovedDCash = false,
    isFailedACH = false,
    hasTooManyDCashReturns = false,
    cardType,
    last4Digits,
    reservationId,
    amounts = {},
    createPaymentResult,
    currentPropertyProfile,
    allowedPayments,
    activeAutoPay,
    accountingDetails,
    balanceDue,
    currentProperty,
    currentPropertyFeeProfile,
    selectedPaymentMethod,
    result,
    fromMobileCheckPay,
    checkTypeProp,
  } = params;
  // These are all bigs
  const type = createPaymentResult?.type;
  const { amount, platformFee, total } = amounts;
  const { street_address, city, state, postal_code, credit_reporting_url } = currentPropertyProfile;
  let { is_integrated } = accountingDetails;
  //Can't rely on activeAutopay.schedule_date property to determine if autopay button should be visible,
  // users setting autopay for first time will always have null for schedule_date.
  //Only integrated users should see autopay button.
  const displayAutoPayButton =
    is_integrated && allowedPayments === AllowedPaymentMethods.ANY && !activeAutoPay?.schedule_date;
  const nickname = selectedPaymentMethod?.nickname;
  const allow_cof = selectedPaymentMethod?.allow_cof;
  const date = getDisplayableLocalTime(new Date()).format("MMMM DD, YYYY");
  const checkData = useSelector((state: RootState) => state.check.checkData);
  const confirmed = useSelector((state: RootState) => state.check.confirmed);
  const duplicatedOrderId = useSelector((state: RootState) => state.check.duplicatedOrderId);
  const currentUserAppState = useSelector((state: RootState) => state.userAppState.userAppState);
  const unsuccessPaymentMessage = fromMobileCheckPay
    ? duplicatedOrderId == null
      ? translate("paymentFlow.paymentTransmitted.yourCheckWasUnsuccessfullyProcessed")
      : translate("paymentFlow.paymentTransmitted.duplicateCheckConfirmationMessage") +
        duplicatedOrderId
    : createPaymentResult?.messageBody || translate("paymentFlow.paymentTransmitted.tryAgain");

  const formattedPaymentDate = formatDate(paymentDate);
  const formattedTotal = toHumanDollars(total);
  const bankInfoTextEnding = isCertifiedBA
    ? `${translate("paymentMethods.certifiedBankAccount.title")}`
    : `${translate("paymentFlow.summary.accountEndingIn")} ****${last4Digits}`;

  const navigateToHome = () => {
    return navigation.navigate("Home", { ...params });
  };
  const navigateToMakeAnotherPayment = () => {
    return navigation.navigate("PaymentFlowPaymentDetails", { ...params });
  };

  const layoutEffect = useCallback(() => {
    dispatch(setPaymentCreated(reservationId));
    //If this is a temporary payment method it will no longer be preselected after payment completes.
    if (!allow_cof) {
      dispatch(setDefaultPaymentSourceUpdated(null));
    }
  }, []);
  useFocusEffect(layoutEffect);

  if (params.certifiedPaymentType === "DCASH_BA") {
    cardType = "Account ending in";
  }
  let isSuccessfulPayment = type === "SUCCESS";
  if (fromMobileCheckPay) {
    isSuccessfulPayment = confirmed;
  }

  const getCheckTypeName = () => {
    return checkTypeProp === CheckType.CERTIFIED
      ? translate("paymentFlow.paymentMethodMobileCheckPay.summary.certifiedCheck")
      : translate("paymentFlow.paymentMethodMobileCheckPay.summary.personalCheck");
  };
  const primaryButtonOnPress = isSuccessfulPayment ? navigateToHome : navigateToMakeAnotherPayment;

  useEffect(() => {}, []);

  const navigateToAutoPayFlow = () => {
    return navigation.navigate("PaymentAutoPayDetails", {
      ...params,
      accountingDetails,
      balanceDue,
      propertyUnit,
      reservationId,
      activeAutoPay,
      currentProperty,
      currentPropertyProfile,
      currentPropertyFeeProfile,
    });
  };

  const getViewHeaderTitle = () => {
    let headerTitle = null;
    if (isSuccessfulPayment) {
      headerTitle = translate("paymentFlow.paymentTransmitted.paymentSubmittedTitle");
    } else {
      if (
        (isUnapprovedDCash || isFailedACH || hasTooManyDCashReturns) &&
        createPaymentResult?.headline
      ) {
        headerTitle = createPaymentResult.headline;
      } else {
        headerTitle = translate("paymentFlow.paymentTransmitted.paymentFailedTitle");
      }
    }
    return headerTitle;
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}>
        <View style={styles.headerSpacer} />
        <View>
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column
                  span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                  style={[styles.inputRow, styles.columnStyle, styles.cardTopStyle]}></Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <Spacer size="large" />
                  <>
                    <Heading
                      heading={getViewHeaderTitle()}
                      isSuccessfulPayment={isSuccessfulPayment}
                    />
                  </>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
          {isSuccessfulPayment ? (
            <>
              <Grid>
                <Container>
                  <Row>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.propertyNameCard}>
                      <Spacer size="medium" />
                      <Row>
                        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                        <Column style={styles.columnStyle} span={{ xs: 12, sm: 8, md: 8, lg: 8 }}>
                          <Text
                            style={styles.propertyName}
                            {...automationTestingLocators.paymentFlowPaymentTrasmitted
                              .propertyName}>
                            {propName}
                          </Text>
                        </Column>
                        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      </Row>
                      <Row>
                        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                        <Column style={styles.columnStyle} span={{ xs: 12, sm: 8, md: 8, lg: 8 }}>
                          <Text
                            style={styles.propertyAddress}
                            {...automationTestingLocators.paymentFlowPaymentTrasmitted
                              .propertyAddress}>
                            {`${street_address}, ${city}, ${state} ${postal_code}\n Unit ${getUnitNumber(
                              propertyUnit
                            )}`}
                          </Text>
                        </Column>
                        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      </Row>
                      <Spacer size="medium" />
                    </Column>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  </Row>
                </Container>
              </Grid>
              <AppReviewModal />
            </>
          ) : (
            <>
              <Grid>
                <Container>
                  <Row>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                      <Spacer size="medium" />
                      <Text
                        style={styles.headerSubTitle2}
                        {...automationTestingLocators.paymentFlowPaymentTrasmitted.supportText}>
                        {unsuccessPaymentMessage}
                      </Text>
                    </Column>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  </Row>
                </Container>
              </Grid>
            </>
          )}
          <Grid>
            <Container>
              {isSuccessfulPayment && (
                <>
                  <Row>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                      <Spacer size="medium" />
                      <View style={styles.inputRowInner}>
                        <Text
                          style={styles.paymentDetailsLeftText}
                          {...automationTestingLocators.paymentFlowPaymentTrasmitted
                            .paymentDateText}>
                          {translate("paymentFlow.summary.paymentDate")}
                        </Text>
                        <Text
                          style={[styles.paymentDetailsRightText]}
                          {...automationTestingLocators.paymentFlowPaymentTrasmitted.paymentDate}>
                          {fromMobileCheckPay ? date : formattedPaymentDate}
                        </Text>
                      </View>
                    </Column>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  </Row>
                  <Row>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                      <Spacer size="medium" />
                      <View style={styles.inputRowInner}>
                        <Text
                          style={styles.paymentDetailsLeftText}
                          {...automationTestingLocators.paymentFlowPaymentTrasmitted
                            .paymentMethodText}>
                          {translate("paymentFlow.summary.paymentMethod")}
                        </Text>
                        <Text
                          style={[styles.paymentDetailsRightText]}
                          {...automationTestingLocators.paymentFlowPaymentTrasmitted
                            .paymentMethod}>{`${
                          fromMobileCheckPay
                            ? getCheckTypeName()
                            : nickname
                            ? nickname.length > 27
                              ? `${nickname.substring(0, 27)}...`
                              : nickname
                            : bankInfoTextEnding
                        }`}</Text>
                      </View>
                    </Column>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  </Row>
                  <Row>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                      <Spacer size="medium" />
                      <View style={styles.inputRowInner}>
                        <Text
                          style={styles.paymentDetailsLeftText}
                          {...automationTestingLocators.paymentFlowPaymentTrasmitted.totalText}>
                          {translate("paymentFlow.summary.total")}
                        </Text>
                        <Text
                          style={styles.paymentDetailsRightText}
                          {...automationTestingLocators.paymentFlowPaymentTrasmitted.total}>
                          {fromMobileCheckPay ? `$${checkData.amount.toFixed(2)}` : formattedTotal}
                        </Text>
                      </View>
                      <View style={{ marginBottom: 25 }} />
                    </Column>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  </Row>
                  {(currentUserAppState == UserAppState.UNIFIED ||
                    currentUserAppState == UserAppState.REWARD_ONLY) && (
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column
                        span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                        style={[styles.inputRow, styles.pointsEarnedCardContainer]}>
                        <View style={[styles.inputRowInner, { flexDirection: "column" }]}>
                          <PaymentSubmittedPointsEarnedCard
                            title={`${translate(
                              "paymentFlow.paymentTransmitted.paymentSubmittedPointsEarnedCard.title"
                            )}`}
                            subTitle={`${translate(
                              "paymentFlow.paymentTransmitted.paymentSubmittedPointsEarnedCard.subTitle1"
                            )} ${200?.toLocaleString("en-US")} ${translate(
                              "paymentFlow.paymentTransmitted.paymentSubmittedPointsEarnedCard.subTitle2"
                            )}`}
                            bodyTitle={`${translate(
                              "paymentFlow.paymentTransmitted.paymentSubmittedPointsEarnedCard.bodyTitle"
                            )}`}
                            bodyDescription={`${translate(
                              "paymentFlow.paymentTransmitted.paymentSubmittedPointsEarnedCard.bodyDescription"
                            )}`}
                            onPressLearnMore={() => {}}
                            {...automationTestingLocators.paymentFlowPaymentTrasmitted
                              .pointsEarnedCard}
                          />
                        </View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                  )}
                  {isSuccessfulPayment && credit_reporting_url && (
                    <>
                      <Row>
                        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                        <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                          <Spacer size="large" />
                          <Text
                            style={styles.footerText1}
                            {...automationTestingLocators.paymentFlowPaymentTrasmitted
                              .buildCreditText}>
                            <Text style={styles.footerText2}>
                              {`${translate("paymentFlow.paymentTransmitted.buildCreditInfo")} `}
                            </Text>
                          </Text>
                        </Column>
                        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      </Row>
                      <Row>
                        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                        <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                          <Text
                            style={styles.footerText1}
                            {...automationTestingLocators.paymentFlowPaymentTrasmitted.signupText}>
                            {`${translate("paymentFlow.paymentTransmitted.signUpInfo1")} `}
                            <Text
                              onPress={async () => {
                                if (await Linking.canOpenURL(credit_reporting_url)) {
                                  await Linking.openURL(credit_reporting_url);
                                }
                              }}
                              style={styles.footerText3}>
                              {translate("paymentFlow.paymentTransmitted.signUpInfo2")}
                            </Text>
                          </Text>
                        </Column>
                        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      </Row>
                    </>
                  )}
                </>
              )}
            </Container>
          </Grid>
        </View>
      </ScrollView>

      <BottomNav backgroundColor={theme.colors.white}>
        <View style={{ marginTop: 5 }} />
        {!isSuccessfulPayment && !isUnapprovedDCash && (
          <Row justifyContent={{ md: "center", lg: "center", sm: "center" }}>
            <Column span={{ xs: 12, lg: 10, md: 10, sm: 10 }}>
              <Button
                textStyle={{ fontSize: 18, fontFamily: "Lato_700Bold" }}
                text={translate("paymentFlow.paymentTransmitted.retryPayment") as string}
                onPress={primaryButtonOnPress}
                type={ButtonType.primary}
                {...automationTestingLocators.paymentFlowPaymentTrasmitted.retryPaymentButton}
              />
            </Column>
          </Row>
        )}
        {isSuccessfulPayment && displayAutoPayButton && (
          <>
            <Row justifyContent={{ md: "center", lg: "center", sm: "center" }}>
              <Column span={{ xs: 12, sm: 10, md: 10, lg: 10 }}>
                <Spacer size="medium" />
                <Button
                  textStyle={{ fontSize: 18, fontFamily: "Lato_700Bold" }}
                  text={
                    translate(
                      "paymentFlow.paymentTransmitted.scheduleRecurringPaymentTransmissionPage"
                    ) as string
                  }
                  onPress={() => {
                    navigateToAutoPayFlow();
                  }}
                  type={"primary"}
                  {...automationTestingLocators.paymentFlowPaymentTrasmitted.recurringPaymentButton}
                />
              </Column>
            </Row>
          </>
        )}
        <Row justifyContent={{ md: "center", lg: "center", sm: "center" }}>
          <Column span={{ xs: 12, sm: 10, md: 10, lg: 10 }}>
            <Spacer size="medium" />
            <Button
              textStyle={{ fontSize: 18 }}
              text={translate("paymentFlow.paymentTransmitted.returnHome") as string}
              onPress={() => {
                navigateToHome();
              }}
              type={"secondary"}
              {...automationTestingLocators.paymentFlowPaymentTrasmitted.backToHomeButton}
            />
          </Column>
        </Row>
      </BottomNav>
      <Footer />
    </SafeAreaView>
  );
};

const Heading = ({ heading, isSuccessfulPayment }: any) => {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
      {isSuccessfulPayment ? (
        <Icon size={45} name="success-filled" color={"#0A850A"} />
      ) : (
        <Icon size={45} name="error-filled" color={theme.colors.alert} />
      )}
      <Text
        style={[
          typography.heading2,
          {
            fontFamily: "Lato_700Bold",
            color: theme.colors.secondary,
          },
        ]}
        {...automationTestingLocators.paymentFlowPaymentTrasmitted.paymentStatusHeading}>
        {heading}
      </Text>
    </View>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: "#FFFFFF",
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: "#FFFFFF",
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: "#FFFFFF",
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 50 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 50 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
    }),
  },
  scrollView: {
    flexGrow: 0,
  },
  headerSpacer: {
    ...domusoResponsiveStyleHelper({
      webPhone: { marginTop: 5 },
      webTablet: { marginTop: 15 },
      webDesktop: { marginTop: 20 },
      iosPhone: { marginTop: 5 },
      iosTablet: { marginTop: 15 },
      androidPhone: { marginTop: 5 },
      androidTablet: { marginTop: 15 },
    }),
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    flexDirection: "row",
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
    }),
  },
  propertyNameCard: {
    paddingVertical: 8,
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FFFFFF" },
      webTablet: {
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: { backgroundColor: "#FAFAFA" },
      iosTablet: {
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: { backgroundColor: "#FAFAFA" },
      androidTablet: {
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  columnStyle: {
    alignItems: "center",
  },
  cardTopStyle: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",
      },
      webDesktop: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  headerSubTitle: {
    ...typography.title2,
    color: theme.colors.text2,
    textAlign: "center",
  },
  propertyName: {
    ...typography.title2,
    color: theme.colors.text4,
    fontFamily: "Lato_700Bold",
    textAlign: "center",
  },
  scheduleRecurringPayment: {
    ...typography.title2,
    color: theme.colors.text4,
    fontFamily: "Lato_400Regular",
    textAlign: "center",
  },
  propertyAddress: {
    ...typography.subHeading,
    color: theme.colors.text2,
    fontFamily: "Lato_700Bold",
    textAlign: "center",
  },
  footerText1: {
    ...typography.subHeading,
    color: theme.colors.text3,
    fontFamily: "Lato_400Regular",
    textAlign: "center",
  },
  footerText2: {
    color: theme.colors.secondary,
    fontFamily: "Lato_700Bold",
  },
  footerText3: {
    color: "#3B53D2",
    textDecorationLine: "underline",
  },
  headerSubTitle2: {
    ...typography.title2,
    color: theme.colors.text4,
    textAlign: "center",
  },
  paymentDetailsLeftText: {
    ...typography.title2,
    color: theme.colors.text3,
    fontWeight: fontWeights.Regular,
    flex: 1,
  },
  paymentDetailsRightText: {
    ...typography.title2,
    color: theme.colors.text3,
    fontWeight: fontWeights.Bold,
    fontFamily: "Lato_700Bold",
    // flex: 0.3,
    textAlign: "right",
    marginLeft: 10,
  },
  pointsEarnedCardContainer: {
    backgroundColor: convertHexToRGBA(theme.colors.success, 0.05),
    paddingVertical: 16,
  },
});

export default PaymentFlowPaymentTrasmitted;
